import moment from 'moment';
import _ from 'lodash';
import { DatePeriod } from './analytics.constants';
export const AnalyticsHelpers = {
    makeAnalyticsClass(componentName) {
        return (name) => {
            if (typeof name === 'string' || typeof name === 'number') {
                return `analytics-${componentName}__${name}`;
            }
            if (_.isArray(name)) {
                return name
                    .filter((item) => typeof item === 'string' || typeof item === 'number')
                    .map((item) => `analytics-${componentName}__${item}`)
                    .join(' ');
            }
            return '';
        };
    },
    makeMeetingHistoryClass(componentName) {
        return (name) => `meeting-history-${componentName}__${name}`;
    },
    rawDataToDate(date) {
        return moment(date).format('ddd MMM D YYYY');
    },
    millisToMinutesAndSeconds(millis) {
        const minutes = Math.floor(millis / 60000) > 9
            ? Math.floor(millis / 60000)
            : `0${Math.floor(millis / 60000)}`;
        const seconds = Number.parseInt(((millis % 60000) / 1000).toFixed(0));
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    millisToMinutesWithoutLeadZeroAndSeconds(millis) {
        const minutes = Math.floor(millis / 60000);
        const seconds = Number.parseInt(((millis % 60000) / 1000).toFixed(0));
        return `${Boolean(minutes) ? minutes : '00'}:${seconds < 10 ? '0' : ''}${seconds}`;
    },
    rawDataToStartTime(date) {
        return moment(date).format('h:mm A');
    },
    getOption(maxValue, maxAmountOfLines, minScale) {
        const stepSize = Number((maxValue / maxAmountOfLines).toFixed(0));
        return {
            plugins: {
                title: {
                    display: false,
                },
                legend: {
                    display: false,
                },
            },
            layout: {
                padding: {
                    left: 15,
                    right: 15,
                },
            },
            responsive: true,
            scales: {
                y: {
                    grid: {
                        drawBorder: false,
                    },
                    ticks: {
                        autoSkip: true,
                        includeBounds: false,
                        color: '#C4C4C4',
                        font: {
                            size: 12,
                            weight: 'bold',
                        },
                        padding: 10,
                        callback: (value) => minScale < value && maxValue === minScale
                            ? undefined
                            : new Intl.NumberFormat(`en-US`).format(value),
                        stepSize,
                    },
                    max: maxValue + stepSize / 2,
                },
                x: {
                    grid: {
                        color: 'transparent',
                        display: false,
                    },
                    ticks: {
                        color: '#C4C4C4',
                        font: {
                            size: 10,
                            weight: 'bold',
                        },
                    },
                },
            },
        };
    },
    createPeriod(options) {
        const now = moment();
        const end = now.clone(); // Today
        let start = now.clone(); // From date
        if (Boolean(options.start)) {
            start.subtract(options.start, 'months');
        }
        if (Boolean(options.end)) {
            end.subtract(options.end, 'months');
        }
        if (Boolean(options.days)) {
            start.subtract(options.days, 'days');
        }
        if (options.allTime) {
            start = moment(0);
        }
        if (!Number.isNaN(Number(options.start) + Number(options.end))) {
            end.set({ date: 31 });
            start.set({ date: 1 });
        }
        else if (!Boolean(options.start) &&
            !Boolean(options.end) &&
            !Boolean(options.days)) {
            start.set({ date: 1 });
        }
        return {
            start: start.set({ hour: 0, minute: 0, second: 0 }).toDate(),
            end: end.set({ hour: 23, minute: 59, second: 59 }).toDate(),
        };
    },
    translateMonth(intl, monthName = '') {
        return intl.formatMessage({ id: `account.analytics.months.${monthName}` });
    },
    optionsForDatepicker(intl) {
        const now = moment();
        return [
            {
                key: DatePeriod.PAST_7_DAYS,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.past-seven-days',
                }),
                days: 6,
            },
            {
                key: DatePeriod.THIS_MONTH,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.this-month',
                }),
                selectLabel: `${this.translateMonth(intl, now.clone().format('MMMM'))} (${intl
                    .formatMessage({ id: `account.analytics.usage.month-to-date` })
                    .toLowerCase()})`,
            },
            {
                key: DatePeriod.PAST_MONTH,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.past-month',
                }),
                start: 1,
                end: 1,
                selectLabel: `${this.translateMonth(intl, now.clone().subtract(1, 'months').format('MMMM'))} (${intl
                    .formatMessage({ id: 'account.analytics.usage.past-month' })
                    .toLowerCase()})`,
            },
            {
                key: DatePeriod.PAST_3_MONTH,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.past-three-months',
                }),
                start: 3,
            },
            {
                key: DatePeriod.PAST_6_MONTH,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.past-six-months',
                }),
                start: 6,
            },
            {
                key: DatePeriod.PAST_12_MONTH,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.past-twelve-months',
                }),
                start: 12,
            },
            {
                key: DatePeriod.ALL_TIME,
                optionLabel: intl.formatMessage({
                    id: 'account.analytics.usage.all-time',
                }),
                allTime: true,
            },
        ];
    },
    formatNumber(num) {
        return new Intl.NumberFormat(`en-US`).format(num);
    },
    addRecentlyUsedOption(option, maxSavedCount) {
        // If same item exists - remove it, then add to the beginning of the list
        const options = this.getRecentlyUsedOptions().filter((item) => item.type !== option.type || item.id !== option.id);
        options.unshift({
            type: option.type,
            id: option.id,
        });
        if (options.length > maxSavedCount) {
            options.length = maxSavedCount;
        }
        localStorage.setItem('analytics.filter.recentlyUsed', JSON.stringify(options));
    },
    getRecentlyUsedOptions() {
        const lsValue = localStorage.getItem('analytics.filter.recentlyUsed');
        if (!lsValue) {
            return [];
        }
        let options = [];
        try {
            options = JSON.parse(lsValue);
        }
        catch {
            return [];
        }
        if (!_.isArray(options)) {
            return [];
        }
        return options
            .filter((option) => {
            return typeof option.type !== 'number' && typeof option.id === 'number';
        })
            .map((option) => {
            return {
                type: option.type,
                id: option.id,
            };
        });
    },
    getNoDataMessage(datePeriod, providersIds, roomsIds, locationIds) {
        switch (true) {
            case datePeriod !== DatePeriod.ALL_TIME &&
                providersIds.length === 0 &&
                roomsIds.length === 0 &&
                locationIds.length === 0:
                return {
                    titleKey: 'account.meeting-history.no-data-for-period.title',
                    messageKey: 'account.meeting-history.no-data-for-period.message',
                };
                break;
            case datePeriod === DatePeriod.ALL_TIME &&
                (providersIds.length > 0 ||
                    roomsIds.length > 0 ||
                    locationIds.length > 0):
                return {
                    titleKey: 'account.meeting-history.no-data-for-provider.title',
                    messageKey: 'account.meeting-history.no-data-for-provider.message',
                };
                break;
            case datePeriod !== DatePeriod.ALL_TIME &&
                (providersIds.length > 0 ||
                    roomsIds.length > 0 ||
                    locationIds.length > 0):
                return {
                    titleKey: 'account.meeting-history.no-data-for-period-and-provider.title',
                    messageKey: 'account.meeting-history.no-data-for-period-and-provider.message',
                };
                break;
            default:
                return {
                    titleKey: 'account.meeting-history.no-meeting-history.title',
                    messageKey: 'account.meeting-history.no-meeting-history.message',
                };
        }
    },
};
